/**
 * Provides inline administration of Integrator users and persists each
 * completed grid edit as one validated replacement of the configured list.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { DataGrid, Column, Editing } from "devextreme-react/data-grid";
import { Switch } from "devextreme-react/switch";
import { Snackbar } from "../../components";
import { LoadPanel } from "devextreme-react/load-panel";
import adminApi from "../../api/admin";
import CustomStore from "devextreme/data/custom_store";

const snackbarType = {
	success: "success",
	error: "error",
};

/**
 * Copies user rows so DevExtreme cannot mutate the last server-confirmed
 * snapshot used to recover from a failed inline save.
 *
 * @param {Array} users User rows to copy.
 * @returns {Array} Independently owned user rows.
 */
const cloneUsers = (users = []) => users.map((user) => ({ ...user }));

const UserAdminSettings = () => {
	const persistedUsersRef = useRef([]);
	const saveInProgressRef = useRef(false);
	const [emailSettings, setEmailSettings] = useState([]);
	const [isLoading, setIsLoading] = useState(true); // Start as true since we fetch on mount
	const [isSubmitting, setIsSubmitting] = useState(false);

	const [snackbarOptions, setSnackbarOptions] = useState({
		visible: false,
		type: "",
		message: "",
	});

	const showNotification = useCallback((message, type) => {
		setSnackbarOptions({
			message,
			type,
			visible: true,
		});
	}, []);

	useEffect(() => {
		let isMounted = true;

		const fetchInitialData = async () => {
			setIsLoading(true);
			try {
				const data = await adminApi.users.getUsersAsync();
				if (isMounted) {
					const loadedUsers = cloneUsers(data || []);
					persistedUsersRef.current = cloneUsers(loadedUsers);
					setEmailSettings(loadedUsers);
				}
			} catch (error) {
				console.error("Failed to load settings:", error);
				if (isMounted) {
					showNotification(
						"Failed to load settings.",
						snackbarType.error
					);
				}
			} finally {
				if (isMounted) setIsLoading(false);
			}
		};

		fetchInitialData();
		return () => {
			isMounted = false;
		};
	}, [showNotification]);

	const handleRowValidating = (e) => {
		const { newData, oldData } = e;
		const isAdmin = newData?.isAdmin ?? oldData?.isAdmin;
		const isBulkEmailAdmin = newData?.isBulkEmailAdmin ?? oldData?.isBulkEmailAdmin;
		const canAccessUi = newData?.canAccessUi ?? oldData.canAccessUi;
		const canAccessApi = newData?.canAccessApi ?? oldData.canAccessApi;
		const isActive = newData?.isActive ?? oldData?.isActive;

		const rowIsNew = e.oldData === undefined || e.oldData.id === 0;

		// Set Id to 0 for new rows, or keep existing Id for updates
		if (rowIsNew) {
			newData.id = 0; // Set Id to 0 for new rows
		} else {
			newData.id = oldData.id; // Preserve the existing Id for updates
		}

		if (
			isAdmin === undefined ||
			isBulkEmailAdmin === undefined ||
			isActive === undefined ||
			canAccessApi === undefined ||
			canAccessUi === undefined
		) {
			e.errorText = "All fields must have a value (true or false).";
			e.isValid = false;
			return;
		}

		// if (!isAdmin && !isBulkEmailAdmin) {
		// 	e.errorText =
		// 		"At least one of IsAdmin or IsBulkEmailAdmin must be true.";
		// 	e.isValid = false;
		// 	return;
		// }
	};

	/**
	 * Persists a complete candidate list and restores the last server-confirmed
	 * snapshot if validation or submission fails.
	 *
	 * @param {Array} candidateUsers Complete grid state after an inline edit.
	 * @returns {Promise<boolean>} Whether the server accepted the update.
	 */
	const persistUsers = useCallback(async (candidateUsers) => {
		const usersToPersist = cloneUsers(candidateUsers);
		const anyActiveAdmins = usersToPersist.some(
			(user) => user.isAdmin && user.isActive
		);
		if (!anyActiveAdmins) {
			setEmailSettings(cloneUsers(persistedUsersRef.current));
			showNotification(
				"Must have at least one active admin.",
				snackbarType.error
			);
			return false;
		}

		if (saveInProgressRef.current) return false;

		saveInProgressRef.current = true;
		setIsSubmitting(true);
		try {
			await adminApi.users.submitUserSettingsAsync(usersToPersist);

			let confirmedUsers = usersToPersist;
			try {
				const refreshedUsers = await adminApi.users.getUsersAsync();
				confirmedUsers = cloneUsers(refreshedUsers || usersToPersist);
			} catch (refreshError) {
				// Submission already succeeded, so retain the accepted candidate
				// rather than incorrectly rolling the UI back to obsolete data.
				console.error(
					"User settings were saved, but could not be refreshed:",
					refreshError
				);
			}

			persistedUsersRef.current = cloneUsers(confirmedUsers);
			setEmailSettings(cloneUsers(confirmedUsers));
			showNotification(
				"Settings were successfully updated.",
				snackbarType.success
			);
			return true;
		} catch (error) {
			setEmailSettings(cloneUsers(persistedUsersRef.current));
			console.error("Error submitting user settings:", error);
			showNotification(
				"Failed to update settings. Please try again.",
				snackbarType.error
			);
			return false;
		} finally {
			saveInProgressRef.current = false;
			setIsSubmitting(false);
		}
	}, [showNotification]);

	/**
	 * Commits the complete DataGrid collection when its inline row Save button,
	 * insertion, or confirmed deletion finishes.
	 *
	 * @param {object} event DevExtreme saved event.
	 * @returns {Promise<boolean>} Whether the server accepted the update.
	 */
	const handleGridSaved = useCallback(
		(event) => {
			const gridDataSource = event?.component?.option?.("dataSource");
			const candidateUsers = cloneUsers(
				Array.isArray(gridDataSource)
					? gridDataSource
					: emailSettings
			);
			setEmailSettings(candidateUsers);
			return persistUsers(candidateUsers);
		},
		[emailSettings, persistUsers]
	);

	const onCloseSnackbar = () => {
		setSnackbarOptions((current) => ({
			...current,
			visible: false,
		}));
	};

	/**
	 * Custom lookup for User field.
	 */
	const userLookupStore = new CustomStore({
		key: "id",
		load: async (loadOptions) => {
			const searchValue = loadOptions.searchValue;
			console.log("load: " + searchValue);
			// Don't call API until 3+ characters
			if (!searchValue || searchValue.length < 3) {
				return [];
			}

			try {
				// Adjust API name & params to match your backend
				const results = await adminApi.users.getUserSuggestionsAsync(searchValue);
				//setSuggestedUsers(results);
				return results; // must be [{ id, label, ... }]
			} catch (error) {
				console.error("User search failed:", error);
				showNotification("Failed to load users.", snackbarType.error);
				return [];
			}
		},
		byKey: async (key) => {
			console.log("byKey:" + key);
			if (!key) return null;

			try {
				// Prefer a single-user endpoint if you have one
				return await adminApi.users.getUserByGraphIdAsync(key);
			} catch (error) {
				console.error("Failed to load user by key:", error);
				return null;
			}
		},
	});

	return (
		<div>
			<h2 className={"content-block"}>User Management</h2>

			<div className="dx-card wide-card">
				<DataGrid
					className={"dx-card wide-card mtb15"}
					dataSource={emailSettings || []}
					disabled={isSubmitting}
					keyExpr="id"
					showBorders={true}
					repaintChangesOnly={true}
					onInitNewRow={(e) => {
						e.data.isAdmin = false;
						e.data.isActive = true;
						e.data.isBulkEmailAdmin = false;
						e.data.canAccessApi = false;
						e.data.canAccessUi = false;
					}}
					onRowValidating={handleRowValidating}
					onSaved={handleGridSaved}
					onEditorPreparing={(e) => {
						if (e.dataField === "email" && e.parentType === "dataRow") {
							var disable = false;
							var dataSource = userLookupStore;
							var value = undefined;

							// Disable is we are editing an existing row.
							if (parseInt(e.row?.key) > 0) {
								disable = true;
								value = e.row.data.email;
								dataSource = [{ id: e.row.data.graphId, label: e.row.data.email }];
							}

							e.editorName = "dxSelectBox";
							e.editorOptions = {
								dataSource: dataSource,
								value: value,
								valueExpr: "label",
								displayExpr: "label",
								searchEnabled: true,
								searchExpr: "label",
								minSearchLength: 3,
								disabled: disable,
								dropDownOptions: {
									width: 500,
								},
								placeholder: "Type at least 3 characters to search",
								onValueChanged: (args) => {
									const selectedUser = args.component.option("selectedItem");

									//Update the email field
									e.setValue(args.value);

									// Update the graphId field
									if (selectedUser) {
										e.component.cellValue(e.row.rowIndex, "graphId", selectedUser.id);
									}
								},
							};
						}
					}}
				>
					<Editing
						mode="row"
						allowUpdating={!isSubmitting}
						allowAdding={!isSubmitting}
						allowDeleting={!isSubmitting}
					/>

					{/* Hidden Id column */}
					<Column dataField="id" visible={false} />

					<Column dataField="email" caption="User Email" width={500} validationRules={[{ type: "required" }]}></Column>

					{/* Add a hidden column for email */}
					<Column dataField="graphId" visible={false} />

					<Column
						dataField="isAdmin"
						caption="Admin"
						dataType="boolean"
						setCellValue={(newData, value) => {
							newData.isAdmin = value;
							if (value === true) {
								newData.canAccessUi = true;
								newData.canAccessApi = true;
								newData.isBulkEmailAdmin = true;
							}
						}}
						cellEditorRender={(cellData) => <Switch value={cellData.value ?? false} onValueChanged={(e) => cellData.setValue(e.value)} />}
					/>
					<Column
						dataField="isBulkEmailAdmin"
						caption="Bulk Email Admin"
						dataType="boolean"
						setCellValue={(newData, value, currentRowData) => {
							if (currentRowData.isAdmin === false) {
								newData.isBulkEmailAdmin = value;
							}
						}}
						cellEditorRender={(cellData) => <Switch value={cellData.value ?? false} onValueChanged={(e) => cellData.setValue(e.value)} />}
					/>
					<Column
						dataField="canAccessUi"
						caption="UI Access"
						dataType="boolean"
						setCellValue={(newData, value, currentRowData) => {
							if (currentRowData.isAdmin === false) {
								newData.canAccessUi = value;
							}
						}}
						cellEditorRender={(cellData) => <Switch value={cellData.value ?? false} onValueChanged={(e) => cellData.setValue(e.value)} />}
					/>
					<Column
						dataField="canAccessApi"
						caption="API Access"
						dataType="boolean"
						setCellValue={(newData, value, currentRowData) => {
							if (currentRowData.isAdmin === false) {
								newData.canAccessApi = value;
							}
						}}
						cellEditorRender={(cellData) => <Switch value={cellData.value ?? false} onValueChanged={(e) => cellData.setValue(e.value)} />}
					/>
					<Column
						dataField="isActive"
						caption="Active"
						dataType="boolean"
						cellEditorRender={(cellData) => <Switch value={cellData.value ?? false} onValueChanged={(e) => cellData.setValue(e.value)} />}
					/>
				</DataGrid>
			</div>

			<LoadPanel
				shadingColor="rgba(0,0,0,0.4)"
				visible={isLoading || isSubmitting}
				showIndicator={true}
				shading={true}
				showPane={true}
				position={{ of: ".dx-card" }}
			/>

			<Snackbar message={snackbarOptions.message} onHiding={onCloseSnackbar} of={".content"} options={snackbarOptions} />
		</div>
	);
};

export default UserAdminSettings;
